import { db, FirebaseUser } from '../../../firebase';
import {
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  setDoc,
  updateDoc,
} from 'firebase/firestore';

import { TENANTS_COLLECTION_NAME } from './constants';
import { trackEvent } from '../server/analytics/trackEvent';
import { UserConfig } from '../../types/user';
import { ArchivedTeam } from '../../types/team';
import { userHasAccessToTeam } from '../../utils/accessUtils';
import { FormationConfig, PlatformFormation } from '../../types/formation';
import { formationConfigsAreEqual, getPlayersWithNewFormation } from '../../utils/formationUtils';


// get all archived teams that the user has access to
export const getAllArchivedTeams = async (
  userConfig: UserConfig,
  currentUser: FirebaseUser,
): Promise<ArchivedTeam[]> => {

  const archivedTeamsCollectionRef = collection(db, TENANTS_COLLECTION_NAME, userConfig.club, 'archivedTeams');

  try {
    const q = query(archivedTeamsCollectionRef, orderBy('date', 'desc'));
    const querySnapshot = await getDocs(q);

    // should move filtering to the query
    return querySnapshot.docs
      .map(doc => ({ ...doc.data(), id: doc.id }) as ArchivedTeam)
      .filter(archivedTeam => userHasAccessToTeam(userConfig, archivedTeam.teamId, archivedTeam.isAcademyTeam ?? false, archivedTeam.teamOwner ?? null));
  }
  catch (error) {
    trackEvent('Error', { api: 'firestore', function: 'getAllArchivedTeams', errorDetails: { error } }, currentUser, 'system');
    return [];
  }
};


// update delete status of given archived team
export const updateArchivedTeamIsDeletedStatus = async (
  archivedTeamId: string,
  isDeleted: boolean,
  tenant: string,
  currentUser: FirebaseUser,
): Promise<boolean> => {

  const archivedTeamDocRef = doc(db, TENANTS_COLLECTION_NAME, tenant, 'archivedTeams', archivedTeamId);

  try {
    await updateDoc(archivedTeamDocRef, {
      isDeleted: isDeleted
    });
    trackEvent(isDeleted ? 'ArchivedTeamDeleted' : 'ArchivedTeamRestored', {}, currentUser, 'user');
    return true;
  }
  catch (error) {
    trackEvent('Error', { api: 'firestore', function: 'updateArchivedTeamIsDeletedStatus', errorDetails: { error } }, currentUser, 'system');
    return false;
  }
};


// update all archived teams that have the currentFormation (with the currentFormationConfig) to newFormation (with the newFormationConfig)
// currentFormation may be the same as newFormation, in which case we have updated the formation config in such a way that the archived teams need to be updated
// currentFormationConfig may be the same as newFormationConfig, in which case we have updated the name of a formation
export const updateArchivedTeamsWithNewFormation = async (
  newFormation: PlatformFormation,
  newFormationConfig: FormationConfig,
  currentFormation: PlatformFormation,
  currentFormationConfig: FormationConfig,
  tenant: string,
  currentUser: FirebaseUser,
) => {

  try {
    const archivedTeamsCollectionRef = collection(db, TENANTS_COLLECTION_NAME, tenant, 'archivedTeams');
    const archivedTeamSnapshots = await getDocs(archivedTeamsCollectionRef);

    archivedTeamSnapshots.forEach(async (docSnapshot) => {

      const archivedTeamId = docSnapshot.id;
      const archivedTeamDoc = docSnapshot.data() as ArchivedTeam;

      if (archivedTeamDoc.formation === currentFormation) {
        const newArchivedTeam: ArchivedTeam = { ...archivedTeamDoc };

        const isNewFormation = newFormation !== archivedTeamDoc.formation;
        if (isNewFormation) {
          newArchivedTeam.formation = newFormation;
        }

        const isNewFormationConfig = !formationConfigsAreEqual(newFormationConfig, currentFormationConfig);
        if (isNewFormationConfig) {
          newArchivedTeam.players = getPlayersWithNewFormation(archivedTeamDoc.players, newFormationConfig, currentFormationConfig);
        }

        const archivedTeamDocRef = doc(db, TENANTS_COLLECTION_NAME, tenant, 'archivedTeams', archivedTeamId);
        await setDoc(archivedTeamDocRef, newArchivedTeam);
      }
    });
  }
  catch (error) {
    trackEvent('Error', { api: 'firestore', function: 'updateTeamsWithNewFormation', errorDetails: { error } }, currentUser, 'system');
  }
};
